<template>
  <div class="vx-row w-full">
    <div class="vx-col w-full">
      <vx-card>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2">
            <LabelForm>{{ $t('apps.snowreportsettings.choose_resort') }}</LabelForm>
            <v-select
              class="w-full md:w-3/4"
              :clearable="false"
              v-model="pendingResort"
              :options="resortOptions"
              :disabled="resorts.length <= 1"
            />
            <div class="mt-4" v-if="resorts.length > 1">
              <vs-button class="rounded-lg" @click="confirmResortSelection" :disabled="!pendingResort">
                {{ $t('question.confirm') }}
              </vs-button>
            </div>
            <div class="mt-4" v-else-if="selectedResort">
              <p class="text-sm text-grey">
                {{ $t('apps.snowreportsettings.settings_for') }}: <strong>{{ selectedResort.label }}</strong>
              </p>
            </div>
          </div>
        </div>

        <div v-if="selectedResort" class="mt-8">
          <div class="flex flex-wrap items-center justify-between mb-4">
            <h3 class="mb-2">{{ $t('apps.snowreportsettings.report_fields') }}</h3>
            <div class="flex flex-wrap gap-2">
              <vs-button type="border" class="rounded-lg" @click="openPreview">
                {{ $t('apps.snowreportsettings.preview') }}
              </vs-button>
              <vs-button class="rounded-lg" @click="saveSettings" :disabled="!hasChanges">
                {{ $t('Save') }}
              </vs-button>
            </div>
          </div>
          <p class="text-sm text-grey mb-4">
            {{ $t('apps.snowreportsettings.settings_for') }}: <strong>{{ selectedResort.label }}</strong>
          </p>

          <div class="vx-row">
            <div class="vx-col w-full lg:w-1/2">
              <h4 class="mb-2">{{ $t('apps.snowreportsettings.enabled_fields') }}</h4>
              <Datatable
                :config="selectedFieldsConfig"
                :elements="selectedFieldsTable"
                :stripe="true"
                :draggable="true"
                :sst="false"
                :customActions="selectedCustomActions"
                @sort="handleSelectedSort"
                @customAction="handleSelectedAction"
              />
              <p v-if="selectedFields.length === 0" class="text-sm text-grey mt-3">
                {{ $t('apps.snowreportsettings.no_fields') }}
              </p>
            </div>
            <div class="vx-col w-full lg:w-1/2">
              <h4 class="mb-2">{{ $t('apps.snowreportsettings.available_fields') }}</h4>
              <Datatable
                :config="availableFieldsConfig"
                :elements="availableFieldsTable"
                :stripe="true"
                :draggable="false"
                :sst="false"
                :customActions="availableCustomActions"
                @customAction="handleAvailableAction"
              />
            </div>
          </div>
        </div>

        <div v-if="selectedResort" class="mt-10">
          <h3 class="mb-4">{{ $t('apps.snowreportsettings.data_provider') }}</h3>
          <div v-if="providerInfo" class="vx-row">
            <div class="vx-col w-full lg:w-1/2">
              <p class="text-base font-semibold">{{ providerInfo.name }}</p>
              <p class="text-sm text-grey">
                {{ $t('apps.snowreportsettings.last_updated') }}:
                <strong>{{ formatTimestamp(providerInfo.last_execution_date || providerInfo.last_execution_date) }}</strong>
              </p>
              <p class="text-sm text-grey">
                {{ $t('apps.snowreportsettings.refresh_freq') }}:
                <strong>{{ providerInfo.refresh_frequency_minutes || providerInfo.refresh_frequency || '-' }}</strong>
                {{ $t('apps.snowreportsettings.time') }}
              </p>
              <vs-button type="border" class="rounded-lg mt-4" @click="forceProviderRefresh">
                {{ $t('apps.snowreportsettings.force_refresh') }}
              </vs-button>
            </div>
            <div class="vx-col w-full lg:w-1/2">
              <h4 class="mb-2">{{ $t('apps.snowreportsettings.last_errors') }}</h4>
              <ul v-if="providerErrors.length" class="text-sm text-grey list-disc pl-5">
                <li v-for="(error, index) in providerErrors" :key="`provider-error-${index}`">
                  {{ error }}
                </li>
              </ul>
              <p v-else class="text-sm text-grey">{{ $t('apps.snowreportsettings.no_errors') }}</p>
            </div>
          </div>
          <p v-else class="text-sm text-grey">{{ $t('apps.snowreportsettings.no_data') }}</p>
        </div>
      </vx-card>
    </div>

    <vs-popup class="snowreport-preview" :active.sync="isPreviewOpen" title="">
      <div class="p-4">
        <h3 class="mb-4">{{ $t('apps.snowreportsettings.preview') }}</h3>
        <div v-if="previewLoading" class="text-sm text-grey">{{ $t('apps.snowreportsettings.preview') }}...</div>
        <div v-else>
          <h4 class="mb-2">{{ $t('apps.snowreportsettings.general_report') }}</h4>
          <div v-if="previewGeneral.length" class="mb-6">
            <div v-for="item in previewGeneral" :key="`general-${item.field}`" class="flex justify-between py-1">
              <span class="font-semibold">{{ item.field }}</span>
              <span class="text-sm text-grey">{{ formatValue(item.value) }}</span>
            </div>
          </div>
          <p v-else class="text-sm text-grey mb-6">{{ $t('apps.snowreportsettings.no_data') }}</p>

          <h4 class="mb-2">{{ $t('resorts.zones.title') }}</h4>
          <div v-if="previewZones.length">
            <div v-for="(zone, index) in previewZones" :key="`zone-${index}`" class="mb-4">
              <p class="font-semibold">{{ zone.name }}</p>
              <div v-if="zone.fields.length" class="ml-4">
                <div v-for="item in zone.fields" :key="`zone-${index}-${item.field}`" class="flex justify-between py-1">
                  <span class="font-semibold">{{ item.field }}</span>
                  <span class="text-sm text-grey">{{ formatValue(item.value) }}</span>
                </div>
              </div>
              <p v-else class="text-sm text-grey ml-4">{{ $t('apps.snowreportsettings.no_data') }}</p>
            </div>
          </div>
          <p v-else class="text-sm text-grey">{{ $t('apps.snowreportsettings.no_data') }}</p>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import Datatable from '@/modules/Shared/Components/table/SKDataTable.vue'
import LabelForm from '@/modules/Shared/Components/form/LabelForm'
import FieldActionButton from '@/modules/Apps/Pages/snowreportsettings/components/FieldActionButton.vue'
import SnowReportSettingsService from '@/modules/Apps/Services/SnowReportSettingsService'
import SnowReportService from '@/modules/Resorts/Services/SnowReportService'
import loader from '@/modules/Shared/Mixins/loader'

const AVAILABLE_FIELDS = [
  'open',
  'max_snow',
  'min_snow',
  'mid_snow',
  'snow_depth',
  'snow_type',
  'last_snowfall',
  'open_lifts',
  'open_slopes',
  'open_skimos',
  'open_crosscountry',
  'preparing_slopes',
  'preparing_lifts',
  'groomed_slopes',
  'groomed_crosscountry',
  'kms_crosscountry',
  'percent_lifts',
  'percent_skiable',
  'skiable_drop',
  'skiable_km',
  'skiable_area',
  'road_condition',
  'snowfall_24',
  'snowfall_48',
  'snowfall_72',
  'snowfall_7',
  'snowfall_season',
  'snowfall_overnight',
  'open_lifts_vs_total',
  'open_skimos_vs_total',
  'open_slopes_vs_total',
  'open_slopes_vs_total_g',
  'open_slopes_vs_total_b',
  'open_slopes_vs_total_r',
  'open_slopes_vs_total_bl',
  'open_activities_vs_total',
  'open_bikepark_circuits_vs_total',
  'open_parkings_vs_total',
  'base_snow',
  'new_snow',
  'treaded_snow',
  'accumulated_snow',
  'temp_min',
  'temp_max',
  'temperature',
  'season_opening_date',
  'season_closing_date',
  'conditions_summary',
  'anouncement',
  'app_fields',
  'grooming',
  'transport',
  'visibility',
  'snowcover',
  'resortsummary',
  'avalanche_risk',
  'report_type',
  'wind',
  'wind_speed',
  'weather_report',
  'parking_status',
  'park_center_status',
  'opening_hours',
  'skiable_km_total',
  'percent_skiable_km',
  'percent_activities',
  'percent_bikeparks',
  'bikeparks_km_open',
  'bikeparks_km_total',
  'bikeparks_km_percentage'
]

export default {
  name: 'snowreport-settings',
  mixins: [loader],
  components: {
    Datatable,
    LabelForm,
    FieldActionButton
  },
  data() {
    return {
      applicationUuid: '',
      resorts: [],
      pendingResort: null,
      selectedResort: null,
      selectedFields: [],
      initialSelectedFields: [],
      settingsExists: false,
      providerId: null,
      providerInfo: null,
      providerErrors: [],
      isPreviewOpen: false,
      previewLoading: false,
      previewGeneral: [],
      previewZones: [],
      selectedFieldsConfig: [
        { field: 'field', head: this.$t('apps.snowreportsettings.enabled_fields'), sortable: false },
        { field: 'actions', type: 'actions', head: this.$t('banner.table.actions'), sortable: false }
      ],
      availableFieldsConfig: [
        { field: 'field', head: this.$t('apps.snowreportsettings.available_fields'), sortable: false },
        { field: 'actions', type: 'actions', head: this.$t('banner.table.actions'), sortable: false }
      ],
      selectedCustomActions: [
        {
          actionName: 'removeField',
          component: FieldActionButton,
          props: {
            icon: 'MinusCircleIcon',
            color: 'danger'
          }
        }
      ],
      availableCustomActions: [
        {
          actionName: 'addField',
          component: FieldActionButton,
          props: {
            icon: 'PlusIcon',
            color: 'primary'
          }
        }
      ]
    }
  },
  computed: {
    resortOptions() {
      return this.resorts.map(resort => ({ label: resort.name, value: resort.uuid }))
    },
    selectedFieldsTable() {
      return this.selectedFields.map(field => ({
        uuid: field,
        field,
        actions: ['removeField']
      }))
    },
    availableFieldsTable() {
      return AVAILABLE_FIELDS.filter(field => !this.selectedFields.includes(field)).map(field => ({
        uuid: field,
        field,
        actions: ['addField']
      }))
    },
    hasChanges() {
      return this.selectedFields.join('|') !== this.initialSelectedFields.join('|')
    }
  },
  async created() {
    this.applicationUuid = this.$route.params.uuid
    await this.load(async () => {
      const resorts = await SnowReportSettingsService.getAppResorts(this.applicationUuid)
      if (Array.isArray(resorts)) {
        this.resorts = resorts
      } else if (resorts && (resorts.data || resorts.object)) {
        this.resorts = resorts.data || resorts.object
      } else {
        this.resorts = []
      }
    })

    if (this.resorts.length === 1) {
      this.pendingResort = this.resortOptions[0]
      await this.applyResortSelection(this.pendingResort)
    }
  },
  methods: {
    async confirmResortSelection() {
      if (!this.pendingResort) return
      if (this.selectedResort && this.pendingResort.value === this.selectedResort.value) return

      if (this.hasChanges) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: this.$t('apps.snowreportsettings.change_resort_warning'),
          text: this.$t('question.sure'),
          accept: () => this.applyResortSelection(this.pendingResort)
        })
        return
      }

      await this.applyResortSelection(this.pendingResort)
    },
    async applyResortSelection(resortOption) {
      this.selectedResort = resortOption
      await this.load(async () => {
        const settings = await SnowReportSettingsService.getResortSettings(resortOption.value)
        const selectedFields = this.extractSelectedFields(settings)
        this.selectedFields = [...selectedFields]
        this.initialSelectedFields = [...selectedFields]
        this.settingsExists = !!settings
        this.providerId = this.extractProviderId(settings)
        await this.loadProviderInfo()
      })
    },
    extractSelectedFields(settings) {
      if (!settings) return []
      const candidates = [
        settings.fields,
        settings.report_fields,
        settings.enabled_fields,
        settings.snowreport_fields,
        settings.report && settings.report.fields,
        settings.settings && settings.settings.fields
      ]
      const found = candidates.find(candidate => candidate && (Array.isArray(candidate) || typeof candidate === 'string'))
      if (!found) return []
      if (Array.isArray(found)) return found
      return found.split(',').map(field => field.trim()).filter(Boolean)
    },
    extractProviderId(settings) {
      if (!settings) return null
      return settings.snowreport_provider_id
        || settings.provider_id
        || settings.data_provider_id
        || (settings.provider && settings.provider.id)
        || (settings.data_provider && settings.data_provider.id)
        || null
    },
    async loadProviderInfo() {
      if (!this.providerId) {
        this.providerInfo = null
        this.providerErrors = []
        return
      }
      const providerInfo = await SnowReportSettingsService.getProviderInfo(this.selectedResort.value)
      this.providerInfo = providerInfo
      const errors = providerInfo
        ? (providerInfo.errors || providerInfo.last_errors || providerInfo.error_log || [])
        : []
      this.providerErrors = Array.isArray(errors)
        ? errors.map(error => (typeof error === 'string' ? error : error.message || JSON.stringify(error)))
        : []
    },
    handleSelectedSort(newOrder) {
      this.selectedFields = newOrder
    },
    handleSelectedAction(action, field) {
      if (action === 'removeField') {
        this.selectedFields = this.selectedFields.filter(selectedField => selectedField !== field)
      }
    },
    handleAvailableAction(action, field) {
      if (action === 'addField') {
        this.selectedFields = [...this.selectedFields, field]
      }
    },
    async saveSettings() {
      if (!this.selectedResort) return
      await this.load(async () => {
        const payload = { fields: this.selectedFields }
        if (this.settingsExists) {
          await SnowReportSettingsService.updateResortSettings(this.selectedResort.value, payload)
        } else {
          await SnowReportSettingsService.createResortSettings(this.selectedResort.value, payload)
          this.settingsExists = true
        }
        this.initialSelectedFields = [...this.selectedFields]
      })
      this.notifySuccess()
    },
    async openPreview() {
      if (!this.selectedResort) return
      this.isPreviewOpen = true
      this.previewLoading = true
      this.previewGeneral = []
      this.previewZones = []

      try {
        const [generalData, zonesData] = await Promise.all([
          SnowReportService.getSnowReport(this.selectedResort.value),
          SnowReportSettingsService.getSnowReportZones(this.selectedResort.value)
        ])
        this.previewGeneral = this.buildPreviewFields(generalData.data[0])
        this.previewZones = this.buildPreviewZones(zonesData)
      } catch (error) {
        console.error(error)
        this.notifyError()
      } finally {
        this.previewLoading = false
      }
    },
    buildPreviewFields(data) {
      return this.selectedFields
        .map(field => ({ field, value: this.getFieldValue(data, field) }))
        .filter(item => this.isValuePresent(item.value))
    },
    buildPreviewZones(zonesData) {
      const zones = this.normalizeZones(zonesData)
      return zones.map((zone, index) => {
        const name = zone.name || zone.title || zone.zone_name || zone.uuid || `${this.$t('resorts.zones.title')} ${index + 1}`
        const fields = this.selectedFields
          .map(field => ({ field, value: this.getFieldValue(zone, field) }))
          .filter(item => this.isValuePresent(item.value))
        return { name, fields }
      }).filter(zone => zone.fields.length > 0)
    },
    normalizeZones(zonesData) {
      if (!zonesData) return []
      if (Array.isArray(zonesData)) return zonesData
      if (Array.isArray(zonesData.zones)) return zonesData.zones
      if (Array.isArray(zonesData.data)) return zonesData.data
      return []
    },
    getFieldValue(data, field) {
      if (!data) return null
      if (Object.prototype.hasOwnProperty.call(data, field)) return data[field]
      const containers = ['report', 'general', 'snow_report', 'snowreport', 'data', 'fields']
      for (const container of containers) {
        if (data[container] && Object.prototype.hasOwnProperty.call(data[container], field)) {
          return data[container][field]
        }
      }
      return null
    },
    isValuePresent(value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'object') return Object.keys(value).length > 0
      return true
    },
    formatValue(value) {
      if (value === null || value === undefined) return '-'
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return JSON.stringify(value)
      return value
    },
    formatTimestamp(value) {
      if (!value) return '-'
      if (typeof value === 'number') return DateTime.fromSeconds(value).toFormat('dd/LL/yyyy HH:mm')
      const iso = DateTime.fromISO(value)
      if (iso.isValid) return iso.toFormat('dd/LL/yyyy HH:mm')
      const sql = DateTime.fromSQL(value)
      if (sql.isValid) return sql.toFormat('dd/LL/yyyy HH:mm')
      return value
    },
    async forceProviderRefresh() {
      if (!this.providerId) return
      await this.load(async () => {
        await SnowReportSettingsService.forceProviderRefresh(this.providerId, {
          last_updated: DateTime.now().toISO(),
          last_updated_at: DateTime.now().toISO()
        })
        await this.loadProviderInfo()
      })
      this.notifySuccess()
    }
  }
}
</script>

<style scoped>
.snowreport-preview .vs-popup--content {
  max-height: 80vh;
  overflow-y: auto;
}
</style>
